/* eslint-disable react/prop-types */
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { del } from '../../../utils/fetchAPI';
import { showErrorToast } from '../../../utils/toaster-alert';
import { swalDel, Toast } from '../../../utils/SwalUti';
import PatientFilesHeader from './PatientFilesHeader';
import PatientFilesToolbar from './PatientFilesToolbar';
import PatientFilesList from './PatientFilesList';
import PatientFilesUploadModal from './PatientFilesUploadModal';
import PatientFilesPreviewModal from './PatientFilesPreviewModal';
import { refId } from './helpers';
import { usePatientMedicalFiles, usePatientFilePreview } from './hooks';
import './patient-files.scss';

const PatientFiles = ({
    visitDetails = {},
    patientDetails = {},
    canUpload = true,
    canDelete = true,
    /** When false (e.g. Files page), upload/list uses patient only; visit is optional on API. */
    requireVisit = true,
}) => {
    const selectedRole = useSelector((state) => state.auth.selectedRole);

    const organizationId = useMemo(() => refId(selectedRole?.organization), [selectedRole]);
    const locationId = useMemo(() => refId(selectedRole?.location), [selectedRole]);

    const patientId = visitDetails?.patient || patientDetails?._id;
    const visitId = visitDetails?._id;

    const [uploadModalShow, setUploadModalShow] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const { scopedFiles, groupedByDate, listLoading, loadMedicalFiles } = usePatientMedicalFiles(
        patientId,
        visitId,
        // When requireVisit is false, disable visit-only filtering in the hook.
        requireVisit
    );

    const { previewOpen, previewUrl, previewLoadingId, openPreview, closePreview } =
        usePatientFilePreview();

    const missingContext =
        !organizationId ||
        !locationId ||
        !patientId ||
        (requireVisit && !visitId);

    const showEmpty = !listLoading && scopedFiles.length === 0;

    const handleDeleteFile = useCallback(
        async (fileId) => {
            const confirm = await swalDel(
                'This file will be permanently removed from storage.'
            );
            if (!confirm.isConfirmed) {
                return;
            }
            setDeletingId(fileId);
            try {
                const res = await del(`files/${fileId}`);
                if (res?.status === 200) {
                    Toast.fire({
                        icon: 'success',
                        title: 'Deleted successfully',
                    });
                    loadMedicalFiles();
                }
            } catch (err) {
                const msg =
                    err?.response?.data?.message || err?.message || 'Could not delete file.';
                showErrorToast(msg);
            } finally {
                setDeletingId(null);
            }
        },
        [loadMedicalFiles]
    );

    return (
        <div className="full-page patient-files">
            <PatientFilesHeader requireVisit={requireVisit} />

            <PatientFilesToolbar
                canUpload={canUpload}
                missingContext={missingContext}
                onOpenUpload={() => setUploadModalShow(true)}
            />

            <PatientFilesList
                listLoading={listLoading}
                showEmpty={showEmpty}
                canUpload={canUpload}
                groupedByDate={groupedByDate}
                previewLoadingId={previewLoadingId}
                deletingId={deletingId}
                onPreview={openPreview}
                onDelete={handleDeleteFile}
                canDelete={canDelete}
                requireVisit={requireVisit}
            />

            <PatientFilesUploadModal
                show={uploadModalShow}
                onClose={() => setUploadModalShow(false)}
                onUploaded={loadMedicalFiles}
                organizationId={organizationId}
                locationId={locationId}
                patientId={patientId}
                visitId={visitId}
                missingContext={missingContext}
            />

            <PatientFilesPreviewModal show={previewOpen} previewUrl={previewUrl} onClose={closePreview} />
        </div>
    );
};

export default PatientFiles;
